import React, { useEffect, useState } from 'react';
import { standingsAPI, seasonsAPI } from '../api/api';
import { Link } from 'react-router-dom';
import SeasonSelector from '../components/SeasonSelector';

const Standings = () => {
  const [standings, setStandings] = useState({ eastern: [], western: [], overall: [] });
  const [seasons, setSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('conferences');
  const [detailView, setDetailView] = useState('basic');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'desc' });

  // Load all seasons once, auto-select active or latest
  useEffect(() => {
    const init = async () => {
      try {
        const res = await seasonsAPI.getAll();
        const all = res.data || [];
        // Sort newest first
        const sorted = [...all].sort((a, b) => b.id - a.id);
        setSeasons(sorted);
        const active = sorted.find(s => s.isActive) || sorted[0];
        if (active) setSelectedSeason(active);
      } catch (err) {
        console.error('Error loading seasons:', err);
        setLoading(false);
      }
    };
    init();
  }, []);

  // Reload standings whenever selected season changes
  useEffect(() => {
    if (!selectedSeason) return;
    loadStandings(selectedSeason);
  }, [selectedSeason]);

  const handleSeasonChange = (id) => {
    const season = seasons.find(s => String(s.id) === String(id));
    if (season) setSelectedSeason(season);
  };

  const loadStandings = async (season) => {
    setLoading(true);
    try {
      if (['top8', 'top16', 'top10_playin', 'custom'].includes(season.playoffFormat)) {
        setView('overall');
      } else {
        setView('conferences');
      }
      const standingsRes = await standingsAPI.get(season.id);
      setStandings(standingsRes.data);
    } catch (error) {
      console.error('Error loading standings:', error);
      setStandings({ eastern: [], western: [], overall: [] });
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const getSortValue = (team, key) => {
    switch (key) {
      case 'winPct': return parseFloat(team.winPct) || 0;
      case 'ppg': return parseFloat(team.ppg) || 0;
      case 'oppg': return parseFloat(team.oppg) || 0;
      case 'diff': return team.gamesPlayed > 0 ? team.pointsDiff / team.gamesPlayed : 0;
      case 'fgPct': return parseFloat(team.fgPct) || 0;
      case 'fg3Pct': return parseFloat(team.fg3Pct) || 0;
      case 'ftPct': return parseFloat(team.ftPct) || 0;
      case 'rpg': return parseFloat(team.rpg) || 0;
      case 'apg': return parseFloat(team.apg) || 0;
      case 'pointsFor': return team.pointsFor || 0;
      case 'pointsAgainst': return team.pointsAgainst || 0;
      case 'pointsDiff': return team.pointsDiff || 0;
      default: return 0;
    }
  };

  const sortTeams = (teams) => {
    if (!sortConfig.key) return teams;
    return [...teams].sort((a, b) => {
      const aVal = getSortValue(a, sortConfig.key);
      const bVal = getSortValue(b, sortConfig.key);
      return sortConfig.direction === 'desc' ? bVal - aVal : aVal - bVal;
    });
  };

  const SortableHeader = ({ label, sortKey, className = '' }) => (
    <th
      className={`px-3 py-3 text-center cursor-pointer select-none hover:bg-gray-200 dark:bg-gray-600 transition ${className}`}
      onClick={() => handleSort(sortKey)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {sortConfig.key === sortKey ? (
          <span className="text-blue-600 dark:text-blue-400">{sortConfig.direction === 'desc' ? '▼' : '▲'}</span>
        ) : (
          <span className="text-gray-300">▼</span>
        )}
      </span>
    </th>
  );

  const renderStandingsTable = (teams, title) => {
    const sortedTeams = sortTeams(teams);
    return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg dark:shadow-gray-900 p-4 md:p-6 mb-6 md:mb-8">
      <h2 className="text-xl md:text-2xl font-bold mb-3 md:mb-4 text-gray-800 dark:text-gray-200">{title}</h2>
      <div className="overflow-x-auto mobile-scroll">
        <table className="w-full text-xs md:text-sm">
          <thead className="bg-gray-100 dark:bg-gray-700">
            <tr>
              <th className="px-3 py-3 text-left sticky left-0 bg-gray-100 dark:bg-gray-700 z-10">#</th>
              <th className="px-3 py-3 text-left sticky left-8 bg-gray-100 dark:bg-gray-700 z-10">Team</th>
              <th className="px-3 py-3 text-center">W-L</th>
              <SortableHeader label="WIN%" sortKey="winPct" />
              
              {detailView === 'advanced' && (
                <>
                  <th className="px-3 py-3 text-center">Home</th>
                  <th className="px-3 py-3 text-center">Away</th>
                  <th className="px-3 py-3 text-center">Streak</th>
                  <SortableHeader label="PPG" sortKey="ppg" />
                  <SortableHeader label="OPP PPG" sortKey="oppg" />
                  <SortableHeader label="DIFF" sortKey="diff" />
                  <SortableHeader label="FG%" sortKey="fgPct" />
                  <SortableHeader label="3P%" sortKey="fg3Pct" />
                  <SortableHeader label="FT%" sortKey="ftPct" />
                  <SortableHeader label="RPG" sortKey="rpg" />
                  <SortableHeader label="APG" sortKey="apg" />
                </>
              )}
              
              {detailView === 'basic' && (
                <>
                  <SortableHeader label="PF" sortKey="pointsFor" />
                  <SortableHeader label="PA" sortKey="pointsAgainst" />
                  <SortableHeader label="DIFF" sortKey="pointsDiff" />
                  <th className="px-3 py-3 text-center">Streak</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {sortedTeams.map((team, index) => (
              <tr key={team.team.id} className="border-b hover:bg-gray-50 dark:bg-gray-800 transition">
                <td className="px-3 py-3 font-semibold sticky left-0 bg-white dark:bg-gray-800">{index + 1}</td>
                <td className="px-3 py-3 sticky left-8 bg-white dark:bg-gray-800">
                  <Link 
                    to={`/teams/${team.team.id}`}
                    className="hover:text-nba-blue transition flex items-center gap-2"
                  >
                    <img 
                      src={`/images/teams/${team.team.name.toLowerCase().replace(/\s/g, '')}.svg`} 
                      alt={`${team.team.name} logo`}
                      className="w-8 h-8 object-contain"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <div>
                      <div className="font-semibold">{team.team.city} {team.team.name}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{team.team.abbreviation}</div>
                    </div>
                  </Link>
                </td>
                <td className="px-3 py-3 text-center font-semibold">
                  <span className="text-green-600 dark:text-green-400">{team.wins}</span>-
                  <span className="text-red-600 dark:text-red-400">{team.losses}</span>
                </td>
                <td className="px-3 py-3 text-center font-bold">{team.winPct}</td>
                
                {detailView === 'advanced' && (
                  <>
                    <td className="px-3 py-3 text-center text-xs">{team.homeRecord}</td>
                    <td className="px-3 py-3 text-center text-xs">{team.awayRecord}</td>
                    <td className="px-3 py-3 text-center">
                      <span className={`font-semibold px-2 py-1 rounded text-xs ${
                        team.streak?.startsWith('W') 
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' 
                          : team.streak?.startsWith('L')
                          ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                      }`}>
                        {team.streak || 'N/A'}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-center font-semibold text-green-600 dark:text-green-400">{team.ppg}</td>
                    <td className="px-3 py-3 text-center font-semibold text-red-600 dark:text-red-400">{team.oppg}</td>
                    <td className="px-3 py-3 text-center font-bold">
                      <span className={parseFloat(team.pointsDiff) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                        {parseFloat(team.pointsDiff) > 0 && '+'}{(team.pointsDiff / team.gamesPlayed).toFixed(1)}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-center">{team.fgPct}%</td>
                    <td className="px-3 py-3 text-center">{team.fg3Pct}%</td>
                    <td className="px-3 py-3 text-center">{team.ftPct}%</td>
                    <td className="px-3 py-3 text-center">{team.rpg}</td>
                    <td className="px-3 py-3 text-center">{team.apg}</td>
                  </>
                )}
                
                {detailView === 'basic' && (
                  <>
                    <td className="px-3 py-3 text-center">{team.pointsFor}</td>
                    <td className="px-3 py-3 text-center">{team.pointsAgainst}</td>
                    <td className="px-3 py-3 text-center">
                      <span className={team.pointsDiff >= 0 ? 'text-green-600 dark:text-green-400 font-semibold' : 'text-red-600 dark:text-red-400 font-semibold'}>
                        {team.pointsDiff > 0 && '+'}{team.pointsDiff}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-center">
                      <span className={`font-semibold px-2 py-1 rounded text-xs ${
                        team.streak?.startsWith('W') 
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' 
                          : team.streak?.startsWith('L')
                          ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                      }`}>
                        {team.streak || 'N/A'}
                      </span>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="text-xl md:text-2xl text-gray-600 dark:text-gray-400">Loading standings...</div>
      </div>
    );
  }

  const hasGames = standings.overall.some(s => s.gamesPlayed > 0);
  const shouldShowTabs = selectedSeason && ['top8', 'top16', 'top10_playin', 'custom'].includes(selectedSeason.playoffFormat);

  return (
    <div className="container mx-auto px-4 py-4 md:py-8">
      <div className="mb-4 md:mb-6 flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-3xl md:text-4xl font-bold">Standings</h1>
          {selectedSeason?.completed && (
            <span className="px-2 py-1 bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-xs font-semibold rounded uppercase tracking-wide">
              Final
            </span>
          )}
          {selectedSeason?.isActive && (
            <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs font-semibold rounded uppercase tracking-wide">
              Live
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <SeasonSelector seasons={seasons} selectedId={selectedSeason?.id} onChange={handleSeasonChange} />
          <Link
            to="/playoffs"
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition"
          >
            🏀 Playoff Picture →
          </Link>
        </div>
      </div>

      {!hasGames && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-400 p-4 md:p-6 rounded-lg mb-4 md:mb-6">
          <h3 className="text-lg md:text-xl font-bold mb-2 text-blue-800">No Games Played Yet</h3>
          <p className="text-sm md:text-base text-gray-700 dark:text-gray-300">
            The season has started but no games have been played yet. Standings will appear 
            once game scores are submitted.
          </p>
        </div>
      )}

      {hasGames && (
        <>
          {/* View Toggle Tabs */}
          <div className="mb-4 md:mb-6">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-1 inline-flex">
              <button
                onClick={() => setView('conferences')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition ${
                  view === 'conferences'
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:bg-gray-700'
                }`}
              >
                By Conference
              </button>
              <button
                onClick={() => setView('overall')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition ${
                  view === 'overall'
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:bg-gray-700'
                }`}
              >
                Overall
                {shouldShowTabs && (
                  <span className="ml-1 px-1.5 py-0.5 bg-green-500 text-white text-xs rounded-full">
                    PLAYOFF
                  </span>
                )}
              </button>
            </div>
            
            {/* Detail View Toggle */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-1 inline-flex ml-3">
              <button
                onClick={() => { setDetailView('basic'); setSortConfig({ key: null, direction: 'desc' }); }}
                className={`px-3 py-2 rounded-md text-xs font-medium transition ${
                  detailView === 'basic'
                    ? 'bg-gray-600 text-white'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:bg-gray-700'
                }`}
              >
                Basic
              </button>
              <button
                onClick={() => { setDetailView('advanced'); setSortConfig({ key: null, direction: 'desc' }); }}
                className={`px-3 py-2 rounded-md text-xs font-medium transition ${
                  detailView === 'advanced'
                    ? 'bg-gray-600 text-white'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:bg-gray-700'
                }`}
              >
                Advanced
              </button>
            </div>
          </div>

          {/* Conference View */}
          {view === 'conferences' && (
            <>
              {renderStandingsTable(standings.eastern, 'Eastern Conference')}
              {renderStandingsTable(standings.western, 'Western Conference')}
            </>
          )}

          {/* Overall View */}
          {view === 'overall' && (
            <>
              {renderStandingsTable(standings.overall, 'Overall Standings')}
              {shouldShowTabs && (
                <div className="bg-green-50 border-l-4 border-green-400 dark:border-green-700 p-4 rounded-lg">
                  <h3 className="text-lg font-bold mb-2 text-green-800">🏆 Playoff Format Info</h3>
                  <p className="text-sm text-green-700 dark:text-green-400">
                    {selectedSeason.playoffFormat === 'top8' && 
                      'Top 8 teams overall will make the playoffs regardless of conference. Standard bracket: 1v8, 2v7, 3v6, 4v5.'}
                    {selectedSeason.playoffFormat === 'top16' && 
                      'Top 16 teams overall will make the playoffs regardless of conference. Standard bracket: 1v16, 2v15, etc.'}
                    {selectedSeason.playoffFormat === 'top10_playin' && 
                      'Top 10 teams overall: Seeds 1-6 advance directly to playoffs. Seeds 7-10 compete in play-in tournament (7v8, 9v10, then elimination game).'}
                    {selectedSeason.playoffFormat === 'custom' && (() => {
                      try {
                        const config = typeof selectedSeason.playoffConfig === 'string' ? JSON.parse(selectedSeason.playoffConfig) : selectedSeason.playoffConfig;
                        if (!config) return 'Custom playoff format.';
                        return `Top ${config.participants} teams ${config.conferenceDependent ? 'per conference' : 'overall'} qualify. Rounds: ${config.rounds.map(r => `${r.name} (${r.seriesFormat})`).join(' → ')}`;
                      } catch (e) { return 'Custom playoff format.'; }
                    })()}
                  </p>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Stats Legend for Advanced View */}
      {detailView === 'advanced' && (
        <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 md:p-6 mb-4 md:mb-6">
          <h3 className="text-base md:text-lg font-bold mb-3">Stats Legend</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 text-xs md:text-sm">
            <div><strong>PPG:</strong> Points Per Game</div>
            <div><strong>OPP PPG:</strong> Opponent Points Per Game</div>
            <div><strong>DIFF:</strong> Point Differential Per Game</div>
            <div><strong>FG%:</strong> Field Goal Percentage</div>
            <div><strong>3P%:</strong> Three-Point Percentage</div>
            <div><strong>FT%:</strong> Free Throw Percentage</div>
            <div><strong>RPG:</strong> Rebounds Per Game</div>
            <div><strong>APG:</strong> Assists Per Game</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Standings;
